
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useState } from "react";
import { useSubscription } from "@/components/premium/hooks/useSubscription";

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { isPremium } = useAuth(); // Now correctly typed
  const { language } = useLanguage();
  const { handleSubscribe, isLoading } = useSubscription();
  const [isManaging, setIsManaging] = useState(false);
  
  const handleManageAccount = () => {
    setIsManaging(true);
    // In a real app with a customer portal, we would redirect to Stripe's customer portal
    // For now, we'll just simulate a loading state and then inform the user
    setTimeout(() => {
      setIsManaging(false);
      navigate('/premium', { 
        state: { 
          message: 'In a production app, this would open the Stripe Customer Portal where you can manage your subscription.'
        }
      });
    }, 1500);
  };
  
  const handleSubscribeClick = () => {
    handleSubscribe();
  };
  
  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
      <Card className="border-2 border-amber-500/30 animate-fade-up animation-delay-100">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('premium.freePlan', language)}</span>
            <span className="text-lg font-normal text-muted-foreground">$0</span>
          </CardTitle>
          <CardDescription>{t('premium.freePlanDescription', language)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{t('premium.freePlanEntryLimit', language)}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? '基本的なエントリー生成' : 'Basic entry generation'}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? 'シンプルな日記インターフェース' : 'Simple diary interface'}</span>
            </li>
            <li className="flex items-start text-muted-foreground">
              <span className="mr-2">✗</span>
              <span>{language === 'ja' ? '成長統計' : 'Growth statistics'}</span>
            </li>
            <li className="flex items-start text-muted-foreground">
              <span className="mr-2">✗</span>
              <span>{language === 'ja' ? '共有機能' : 'Sharing capabilities'}</span>
            </li>
          </ul>
        </CardContent>
        <CardFooter>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/entries')}
          >
            {t('premium.currentPlan', language)}
          </Button>
        </CardFooter>
      </Card>

      <Card className="relative border-2 border-primary animate-fade-up animation-delay-200">
        <div className="absolute -top-3 -right-3">
          <Badge variant="default" className="bg-amber-500 hover:bg-amber-600 flex items-center gap-1">
            <Crown className="h-3.5 w-3.5" />
            <span>{t('premium.recommended', language)}</span>
          </Badge>
        </div>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('premium.premiumPlan', language)}</span>
            <span className="text-lg font-normal text-muted-foreground">$1.99/mo</span>
          </CardTitle>
          <CardDescription>{t('premium.premiumPlanDescription', language)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? '無料プランのすべての機能' : 'Everything in Free plan'}</span>
            </li>
            <li className="flex items-start font-medium">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? '無制限の日記エントリー' : 'Unlimited diary entries'}</span>
            </li>
            <li className="flex items-start font-medium">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? '成長統計とチャート' : 'Growth statistics and charts'}</span>
            </li>
            <li className="flex items-start font-medium">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? '家族とエントリーを共有' : 'Share entries with family'}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{language === 'ja' ? 'プライオリティサポート' : 'Priority support'}</span>
            </li>
          </ul>
        </CardContent>
        <CardFooter>
          {isPremium ? (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleManageAccount}
              disabled={isManaging}
            >
              {isManaging ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {language === 'ja' ? '読み込み中...' : 'Loading...'}
                </>
              ) : (
                language === 'ja' ? 'サブスクリプションを管理' : 'Manage Subscription'
              )}
            </Button>
          ) : (
            <Button 
              className="w-full"
              onClick={handleSubscribeClick}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {language === 'ja' ? '読み込み中...' : 'Loading...'}
                </>
              ) : (
                t('premium.subscribeNow', language)
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionPlans;
